/// «Послуги» — every word of the section, in its own file so that it merges
/// cleanly next to other sections (uk.ts carries one line for it).
/// Sentence case; plurals through `plural`.

const plural = (n: number, one: string, few: string, many: string): string => {
  const mod10 = n % 10
  const mod100 = n % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

const minutes = (n: number) => `${n} хв`
const providers = (n: number) => `${n} ${plural(n, 'виконавець', 'виконавці', 'виконавців')}`
const rows = (n: number) => `${n} ${plural(n, 'рядок', 'рядки', 'рядків')}`
const classes = (n: number) => `${n} ${plural(n, 'заняття', 'заняття', 'занять')}`
const places = (n: number) => `${n} ${plural(n, 'місце', 'місця', 'місць')}`
const people = (n: number) => `${n} ${plural(n, 'записаний', 'записані', 'записаних')}`
/// «Бокс 6–10» — the class named in a refusal, or «це заняття» without one.
const which = (name: string | null) => (name ? `«${name}»` : 'це заняття')
/// «(6–10 років)» after the class — nothing when the band is unknown.
const forAges = (band: string | null) => (band ? ` — заняття для віку ${band}` : '')

export const servicesUk = {
  nav: 'Послуги',
  title: 'Послуги',
  tabs: { calendar: 'Календар', timetable: 'Розклад', catalogue: 'Послуги', hours: 'Графік роботи', analytics: 'Аналітика' },
  subtitles: {
    calendar: 'Записи й заняття виконавців: хто зайнятий, а хто вільний. Вільна клітинка — новий запис.',
    timetable: 'Групові заняття, що повторюються щотижня. Календар заповнюється на чотири тижні вперед сам.',
    catalogue: 'Що продає клуб: ціна, тривалість, хто надає послугу і скільки отримує.',
    hours: 'Коли кожен виконавець працює. Поза цими годинами на нього не запишуть.',
    analytics: 'Скільки заробили виконавці й компанія за місяць.',
    analyticsOwn: 'Ваш заробіток за місяць — із ваших записів і занять.',
  },
  branch: 'Клуб',
  allBranches: 'Усі клуби',
  ownClub: 'Без клубу',
  provider: 'Виконавець',
  allProviders: 'Усі виконавці',
  you: 'ви',
  unnamed: 'Без імені',
  unknownService: 'Послуга',
  kinds: { personal: 'Індивідуальна', group: 'Групова' },
  minutes,
  providers,

  payment: {
    unpaid: 'Не оплачено',
    paid: 'Оплачено',
    refunded: 'Повернуто',
    pass: 'За абонементом',
    free: 'Без оплати',
    void: 'Нічого не винні',
  } as Record<string, string>,
  methods: { cash: 'готівкою', card: 'карткою', online: 'онлайн' } as Record<string, string>,
  bookingStatus: {
    booked: 'Записаний',
    cancelled: 'Скасовано',
    attended: 'Прийшов',
    no_show: 'Не прийшов',
  } as Record<string, string>,
  priceSource: {
    service: 'ціна послуги',
    provider: 'ціна виконавця',
    desk: 'ціна на рецепції',
  } as Record<string, string>,

  calendar: {
    today: 'Сьогодні',
    prevWeek: 'Попередній тиждень',
    nextWeek: 'Наступний тиждень',
    prevDay: 'Попередній день',
    nextDay: 'Наступний день',
    views: { label: 'Вигляд календаря', day: 'День', week: 'Тиждень' },
    grid: (week: string) => `Тиждень ${week}`,
    gridDay: (day: string) => `День ${day}, по виконавцях`,
    openDay: (day: string) => `Відкрити день: ${day}`,
    nobody: 'Без виконавця',
    gridHint:
      'Стрілками — між клітинками, Enter — записати чи поставити заняття на цей час. Записи — окремі кнопки в межах дня. ' +
      'Поза сіткою: T — сьогодні, стрілки вліво й вправо або J і K — попередній і наступний період, D — день, W — тиждень, G — перейти до дати.',
    cell: (day: string, time: string) => `${day}, ${time} — вільно, записати`,
    cellPast: (day: string, time: string) => `${day}, ${time} — минулий час`,
    jump: 'Перейти до дати',
    /// A toolbar button's tooltip with its key: «Сьогодні (T)».
    withKey: (label: string, key: string) => `${label} (${key})`,
    entries: (n: number) => `${n} ${plural(n, 'запис', 'записи', 'записів')}`,
    openDayWith: (day: string, entries: string) => `Відкрити день: ${day}, ${entries}`,
    cellReadOnly: (day: string, time: string) => `${day}, ${time}`,
    working: 'робочий час',
    offWork: 'неробочий час',
    timeOff: 'Відсутність',
    buffer: (n: number) => `+ ${minutes(n)} перерва`,
    cancelled: 'скасовано',
    pickProviderForHours: 'Оберіть виконавця, щоб бачити його робочі години.',
    empty: 'Цього тижня записів немає',
    emptyDay: 'Цього дня записів немає',
    emptyMessage: 'Натисніть на вільний час у сітці, щоб записати клієнта або поставити заняття.',
    emptyReadOnly: 'Записи зʼявляться тут, щойно клієнтів запишуть.',
    noServices: 'Ще немає послуг',
    noServicesMessage: 'Додайте послугу — масаж, персональне тренування чи групове заняття — у вкладці «Послуги».',
    scrollHint: 'Сітку можна гортати вбік.',
    legendBuffer: 'перерва після запису',
    legendOff: 'поза робочим часом',
    legendPersonal: 'запис у кольорі послуги',
    legendGroup: 'групове заняття: місць у ньому',
    legendCancelled: 'скасовано',
    showCancelled: 'Показати скасовані',
    placesUpTo: (n: number) => `до ${places(n)}`,
  },

  timetable: {
    add: 'Нове заняття',
    grid: 'Тижневий розклад',
    gridHint: 'Стрілками — між клітинками, Enter — нове заняття на цей час. Заняття — окремі кнопки в межах дня.',
    cell: (day: string, time: string) => `${day}, ${time} — додати заняття`,
    cellReadOnly: (day: string, time: string) => `${day}, ${time}`,
    days: ['Неділя', 'Понеділок', 'Вівторок', 'Середа', 'Четвер', 'Пʼятниця', 'Субота'],
    shortDays: ['Нд', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'],
    slotLabel: (name: string, day: string, from: string, to: string, who: string) => `${name}, ${day} ${from}–${to}, ${who}`,
    noTrainer: 'Тренера не вказано',
    noRoom: 'Без залу',
    places,
    classes,
    from: (date: string) => `з ${date}`,
    until: (date: string) => `до ${date}`,
    empty: 'Розклад ще порожній',
    emptyMessage: 'Додайте групове заняття, що повторюється щотижня, — календар заповниться на чотири тижні вперед.',
    emptyReadOnly: 'Розклад складає керуючий.',
    noGroup: 'Ще немає групових послуг',
    noGroupMessage: 'Розклад складається з групових послуг. Додайте послугу виду «Групова» у вкладці «Послуги».',
    readOnly: 'Ви бачите розклад, але змінювати його може роль із правом «Розклад: керування».',
    ownReadOnly: 'Тут лише ваші заняття. Розклад складає керуючий.',
    ended: 'Зняті з розкладу',
    endedFootnote: 'Ці заняття більше не повторюються. Ті, що вже відбулися, лишилися в календарі.',
    endedOn: (date: string) => `останнє ${date}`,
    edit: 'Змінити',
    retire: 'Зняти з розкладу',
    scrollHint: 'Сітку можна гортати вбік.',
    // the side panel
    newTitle: 'Нове заняття в розкладі',
    editTitle: (name: string) => name,
    newDescription: 'Повторюється щотижня. Заняття на чотири тижні вперед зʼявляться в календарі одразу.',
    editDescription: 'Зміни діють від сьогодні. Заняття, на які вже записалися, лишаться як були.',
    service: 'Заняття',
    pickService: 'Оберіть заняття',
    weekdays: 'Дні',
    weekday: 'День',
    weekdaysHint: 'Кожен день — окремий рядок розкладу: його можна змінити чи зняти окремо.',
    pickWeekday: 'Оберіть хоча б один день',
    time: 'Початок',
    timeRequired: 'Вкажіть час початку',
    duration: 'Тривалість',
    ends: (time: string) => `Закінчується о ${time}`,
    trainer: 'Тренер',
    pickTrainer: 'Тренера не вказано',
    room: 'Зал',
    roomNone: 'Без залу — зал не перевіряється',
    roomHint: 'Із залом клуб не поставить два заняття в одне приміщення одночасно.',
    roomOf: (name: string, capacity: number) => `${name} · до ${places(capacity)}`,
    capacity: 'Місць',
    capacityInvalid: 'Від 1 до 10 000',
    roomTooSmall: (capacity: number) => `У залі лише ${places(capacity)}`,
    activeFrom: 'Діє з',
    activeUntil: 'Діє до',
    activeHint: 'Порожньо — з сьогодні й без кінця.',
    rangeInvalid: '«Діє до» має бути не раніше за «Діє з»',
    branch: 'Клуб',
    wholeCompany: 'Усі клуби компанії',
    create: (n: number) => (n > 1 ? `Додати ${n} заняття` : 'Додати заняття'),
    save: 'Зберегти',
    saving: 'Зберігаємо…',
    noChanges: 'Нічого не змінено.',
    // results
    created: (name: string, n: number, generated: number) =>
      `«${name}» додано в розклад${n > 1 ? ` на ${n} ${plural(n, 'день', 'дні', 'днів')}` : ''}. У календарі — ${classes(generated)} на чотири тижні вперед.`,
    createdPartly: (made: string, failed: string, why: string) => `Додано: ${made}. ${failed} — не вдалося: ${why}`,
    conflicts: (n: number) =>
      `${classes(n)} не ${plural(n, 'потрапило', 'потрапили', 'потрапили')} в календар: тренер або зал у цей час уже зайняті. Перевірте календар.`,
    updated: (name: string) => `«${name}» змінено від сьогодні.`,
    moved: (n: number) => `${classes(n)} без записів перенесено на новий час.`,
    withdrawn: (n: number) => `${classes(n)} без записів прибрано з календаря.`,
    retiredBanner: (name: string) => `«${name}» знято з розкладу.`,
    keptTitle: (n: number) => `${classes(n)} ${plural(n, 'лишилося', 'лишилися', 'лишилися')} в календарі як ${plural(n, 'було', 'були', 'були')}`,
    keptHint:
      'На них уже записалися або їх змінювали вручну. Якщо заняття не буде — скасуйте його в календарі: записи на нього скасуються, а там же буде видно, кому повернути гроші.',
    keptBooked: (n: number) => people(n),
    keptEdited: 'змінено вручну',
    openCalendar: 'Відкрити в календарі',
    dismiss: 'Сховати',
    // retire
    retireTitle: (name: string, day: string, time: string) => `Зняти «${name}» (${day}, ${time}) з розкладу?`,
    retireMessage:
      'Заняття більше не повторюватиметься. Майбутні заняття без записів зникнуть із календаря. Ті, на які вже записалися, лишаться — ми покажемо, які саме, щоб ви вирішили, що з ними робити.',
    retireConfirm: 'Зняти з розкладу',
    // clashes (S2, S3): the server skips the second class, every week
    clashTitle: (n: number) =>
      n > 1 ? `${n} ${plural(n, 'пара занять накладається', 'пари занять накладаються', 'пар занять накладаються')}` : 'Заняття накладаються',
    clashPair: (a: string, aWhen: string, b: string, bWhen: string, why: string) => `«${a}» (${aWhen}) і «${b}» (${bWhen}) — ${why}.`,
    clashByTrainer: (name: string) => `той самий тренер, ${name}`,
    clashByRoom: (room: string) => `той самий зал, ${room}`,
    clashHint:
      'Одному тренерові чи в одному залі двох занять одночасно не буде: одне з пари щотижня не потрапляє в календар, і клієнти його не бачать. Змініть час, тренера чи зал.',
    clash: (others: readonly string[]) => `Накладається на ${others.join(', ')} — одне з них не потрапляє в календар`,
    clashWith: (name: string, day: string, time: string) => `«${name}» (${day} ${time})`,
  },

  session: {
    kind: 'Що створити',
    kinds: { appointment: 'Запис до виконавця', group: 'Групове заняття' },
    title: 'Групове заняття',
    description: 'Разове заняття на цю дату — не повторюється. Щотижневе додають у вкладці «Розклад».',
    service: 'Заняття',
    noGroup: 'Немає групових послуг. Додайте їх у вкладці «Послуги».',
    trainer: 'Тренер',
    pickTrainer: 'Тренера не вказано',
    date: 'Дата',
    time: 'Початок',
    submit: 'Створити заняття',
    submitting: 'Створюємо…',
    created: (name: string, time: string) => `«${name}» поставлено на ${time}.`,
  },

  age: {
    tooYoung: (name: string | null, band: string | null) =>
      `За віком клієнтові ще рано на ${which(name)}${forAges(band)}.`,
    tooOld: (name: string | null, band: string | null) =>
      `За віком клієнт уже не підходить для ${which(name)}${forAges(band)}.`,
    birthDateRequired: (name: string | null, band: string | null) =>
      `У ${which(name)} є обмеження за віком${band ? ` (${band})` : ''}, а в картці клієнта немає дати народження. Додайте її в розділі «Клієнти» й запишіть ще раз.`,
  },

  book: {
    title: 'Новий запис',
    description: 'Клієнт оплачує в клубі — запис буде «Не оплачено», доки рецепція не прийме оплату.',
    service: 'Послуга',
    pickService: 'Оберіть послугу',
    noPersonal: 'Немає індивідуальних послуг із виконавцями.',
    provider: 'Виконавець',
    pickProvider: 'Оберіть виконавця',
    noProviders: 'У цієї послуги ще немає виконавців.',
    date: 'Дата',
    time: 'Час',
    freeTimes: 'Вільний час цього дня',
    freeTimesNone: 'Цього дня в цього виконавця вільного часу немає.',
    freeTimesHint: 'Підказка з графіка — остаточно вирішує запис.',
    client: 'Клієнт',
    pickClient: 'Оберіть клієнта',
    price: 'Ціна',
    priceList: (list: string) => `Ціна за прайсом — ${list}.`,
    priceNone: 'Послуга лише за абонементом — тут її можна записати тільки з ціною разового візиту.',
    priceOff: (off: number, list: string) => `Знижка ${String(off).replace('.', ',')} % від ${list}.`,
    priceAbove: (list: string) => `Вище прайсу (${list}).`,
    priceInvalid: 'Вкажіть ціну цифрами, наприклад 800 або 800,50',
    priceOverLimit: (pct: number) => `Ваша роль дає знижку не більше ${pct} %. Попросіть керуючого.`,
    ends: (time: string, buffer: string) => `До ${time}${buffer}`,
    endsBuffer: (n: number) => `, потім ${minutes(n)} перерви`,
    submit: 'Записати',
    submitting: 'Записуємо…',
    booked: (client: string, time: string) => `${client} записаний на ${time}.`,
  },

  booking: {
    title: 'Запис',
    group: 'Групове заняття',
    when: 'Коли',
    provider: 'Виконавець',
    service: 'Послуга',
    client: 'Клієнт',
    clients: 'Записані',
    clientsNone: 'На це заняття ще ніхто не записаний.',
    price: 'Ціна',
    noPrice: 'Без ціни',
    payment: 'Оплата',
    paidAt: (method: string, at: string) => `Оплачено ${method}, ${at}`,
    refundedAt: (at: string) => `Повернуто ${at}`,
    payCash: 'Оплачено готівкою',
    payCard: 'Оплачено карткою',
    refund: 'Повернути гроші',
    refundTitle: (amount: string) => `Повернути ${amount}?`,
    refundMessage: 'Запис скасовано, гроші повертаються клієнтові повністю. Скасувати повернення не можна.',
    attended: 'Прийшов',
    noShow: 'Не прийшов',
    cancel: 'Скасувати запис',
    cancelTitle: 'Скасувати запис?',
    cancelMessage:
      'Час виконавця звільниться одразу. Якщо клієнт уже заплатив — після скасування можна повернути гроші.',
    cancelConfirm: 'Скасувати запис',
    late: 'Скасовано пізно',
    sessionCancelled: 'Це заняття скасоване.',
    places: (taken: number, capacity: number) => `${taken} з ${places(capacity)}`,
    addClient: 'Записати клієнта',
    addClientTitle: 'Записати на заняття',
    addSubmit: 'Записати',
    addPriceHint: (list: string) => `Ціна разового візиту за прайсом — ${list}.`,
    addPriceNone: 'Заняття лише за абонементом, а діючого абонемента в клієнта немає. Щоб записати разово, вкажіть ціну візиту.',
    paysWith: 'Чим оплачує',
    oneOff: 'Разовий візит',
    oneOffNoPrice: 'Без абонемента — з ціною, яку ви вкажете',
    noPass: 'Діючого абонемента немає — запис як разовий візит.',
    passesLoading: 'Шукаємо абонементи клієнта…',
    passesFailed: (why: string) => `Не вдалося перевірити абонементи клієнта: ${why} Запис буде як разовий візит.`,
    added: (client: string) => `${client} записаний на заняття.`,
    ageBand: (band: string) => `Заняття для віку ${band} — вік перевіряється за датою народження з картки клієнта.`,
    noActions: 'Оплату й скасування веде рецепція — у вашої ролі немає продажу.',
    done: {
      paid: 'Оплату прийнято.',
      refunded: 'Гроші повернуто.',
      cancelled: 'Запис скасовано, час звільнено.',
      attended: 'Позначено: прийшов.',
      no_show: 'Позначено: не прийшов.',
    } as Record<string, string>,
  },

  /// «Скасувати заняття» and «Замінити тренера» on one group class
  /// (specs/schedule.md §5).
  sessionOps: {
    cancel: 'Скасувати заняття',
    trainer: 'Замінити тренера',
    back: 'Назад',
    cancelTitle: 'Скасувати заняття?',
    cancelLead: 'Заняття не відбудеться: усі записи на нього скасуються, черга закриється, а записаним сформується повідомлення.',
    booked: 'Записані',
    bookedNone: 'На заняття ніхто не записаний — повідомляти й повертати нікому.',
    summary: (booked: number, paid: number, paidSum: string) =>
      paid > 0 ? `${people(booked)} · оплатили ${paid} на ${paidSum}` : `${people(booked)} · окремо ніхто не платив`,
    reason: 'Причина',
    reasonPlaceholder: 'Тренерка захворіла',
    reasonHint: 'Необовʼязково. Записані прочитають її в повідомленні про скасування.',
    reasonLong: (max: number) => `Не довше ${max} символів`,
    moneyNote: (paid: number, sum: string) =>
      `Гроші самі не повертаються: після скасування поверніть кожному з ${paid} оплачених — разом ${sum}.`,
    passNote: (n: number) => `За абонементом — ${n}: візит не списувався, повертати нічого.`,
    deliveryNote: 'Повідомлення формуються автоматично, але SMS і Viber ще не підключені — поки що попередьте записаних самі.',
    deliveryShort: 'SMS і Viber ще не підключені — поки що попередьте їх самі.',
    cancelConfirm: 'Скасувати заняття',
    cancelling: 'Скасовуємо…',
    cancelled: (n: number, reason: string | null) =>
      [
        'Заняття скасовано.',
        reason ? `Причина: ${reason}.`.replace(/([.!?…])\.$/, '$1') : null,
        n > 0 ? `${n} ${plural(n, 'запис знято', 'записи знято', 'записів знято')}, черга закрита.` : null,
      ]
        .filter(Boolean)
        .join(' '),
    phoneUnknown: 'Телефону немає',

    refunds: 'Повернути гроші',
    refundsMeta: (paid: number, sum: string) => `${paid} · ${sum}`,
    refundsFooter: 'Нічого не повертається саме: кожне повернення — окремо й з підтвердженням.',
    refundRow: 'Повернути',
    refundedRow: 'Повернуто',
    overLimit: 'Понад ваш ліміт',
    limitText: (limit: string, amount: string) => `Ваша роль повертає до ${limit}, а тут ${amount}.`,
    approvers: (amount: string, n: number) => `Повернути ${amount} ${n === 1 ? 'може' : 'можуть'}:`,
    approversLoading: 'Шукаємо, хто може повернути…',
    approversNone: 'У компанії ніхто не може повернути таку суму — власник може змінити ліміти в «Ролях».',
    approversFailed: 'Не вдалося дізнатися, хто може повернути. Спробуйте ще раз.',
    noSell: 'Повертає гроші рецепція — у вашої ролі немає продажу.',

    trainerTitle: 'Замінити тренера',
    trainerNow: (who: string) => `Зараз веде ${who}`,
    pick: 'Хто проведе',
    pickRequired: 'Оберіть, хто проведе заняття',
    providersGroup: (service: string) => `Ведуть «${service}»`,
    othersGroup: 'Інші співробітники',
    busy: (what: string, time: string) => `Веде «${what}» о ${time}`,
    away: (note: string | null) => (note ? `Відсутність: ${note}` : 'Відсутність'),
    hintAdvice: 'Зайнятість — підказка з календаря. Остаточно перевіряє запис: двох занять одночасно в людини не буде.',
    nobodyElse: 'Більше нікому — додайте співробітника в розділі «Співробітники».',
    trainerNotify: (n: number) =>
      n > 0
        ? `${n} ${plural(n, 'записаному', 'записаним', 'записаним')} сформується повідомлення про заміну.`
        : 'На заняття ще ніхто не записаний — повідомляти нікого.',
    trainerConfirm: 'Замінити тренера',
    changing: 'Замінюємо…',
    changed: (who: string, n: number) =>
      n > 0
        ? `Тепер веде ${who}. ${n} ${plural(n, 'записаному', 'записаним', 'записаним')} сформовано повідомлення про заміну.`
        : `Тепер веде ${who}.`,
    clash: (who: string, what: string, when: string) => `${who} у цей час уже веде «${what}» — ${when}. Оберіть іншого тренера.`,
    clashAppointment: (who: string, what: string, when: string) =>
      `${who} у цей час має запис «${what}» — ${when}. Оберіть іншого тренера.`,
  },

  catalogue: {
    add: 'Нова послуга',
    active: 'У продажу',
    retired: 'Зняті з продажу',
    retiredFootnote: 'Зняті послуги не продаються. Уже зроблені записи лишаються з тією ціною, за якою їх продали.',
    empty: 'Ще немає жодної послуги',
    emptyMessage: 'Додайте першу — масаж, персональне тренування чи групове заняття з ціною.',
    emptyReadOnly: 'Послуги додає власник або директор.',
    readOnly: 'Ви бачите послуги, але змінювати їх може роль із правом «Розклад: керування» на всю компанію.',
    columns: {
      name: 'Послуга',
      duration: 'Тривалість',
      price: 'Ціна',
      providers: 'Виконавці',
      payout: 'Виконавцю',
      online: 'У застосунку',
      ages: 'Вік',
    },
    durationWithBuffer: (duration: number, buffer: number) =>
      buffer > 0 ? `${minutes(duration)} + ${minutes(buffer)}` : minutes(duration),
    byPass: 'Лише за абонементом',
    online: 'Продається',
    offline: '—',
    noProviders: 'Немає',
    noPayout: 'Не задано',
    generalPayout: 'За загальними правилами',
    edit: 'Змінити',
    retire: 'Зняти з продажу',
    retireTitle: (name: string) => `Зняти «${name}» з продажу?`,
    retireMessage:
      'Послугу більше не можна буде купити ні на рецепції, ні в застосунку. Записи, що вже є, лишаються.',
    retireConfirm: 'Зняти з продажу',
    retiredBanner: (name: string) => `«${name}» знято з продажу.`,
    savedBanner: (name: string) => `«${name}» збережено.`,
    noCategory: 'Без категорії',
    ages: (band: string) => band,
  },

  service: {
    newTitle: 'Нова послуга',
    editTitle: (name: string) => name,
    name: 'Назва',
    namePlaceholder: 'Масаж спини 60 хв',
    nameRequired: 'Вкажіть назву',
    kind: 'Як продається',
    kindHint: {
      personal: 'Один клієнт, один виконавець — запис у вільний час.',
      group: 'Один виконавець, багато клієнтів — на заняття за розкладом.',
    },
    kindFixed: 'Вид послуги після створення не змінюється.',
    duration: 'Тривалість',
    buffer: 'Перерва після',
    bufferHint: 'Час, щоб підготуватися до наступного клієнта. Наступний запис не почнеться раніше.',
    capacity: 'Місць на занятті',
    price: 'Ціна для клієнта',
    priceHint: 'Порожньо — лише за абонементом.',
    priceInvalid: 'Вкажіть ціну цифрами або залиште порожнім',
    description: 'Опис',
    descriptionHint: 'Один абзац — його показує застосунок.',
    category: 'Категорія',
    categoryPlaceholder: 'Групові заняття',
    categoryHint: 'Розділ, під яким послугу видно в застосунку й у цьому списку. Оберіть наявну або впишіть нову.',
    image: 'Картинка',
    imageHint: 'Показується в застосунку на картці послуги. PNG, JPEG або WebP, до 5 МБ; краще горизонтальна.',
    imageDrop: 'Відпустіть, щоб завантажити',
    imageChoose: 'Обрати картинку',
    imageReplace: 'Замінити',
    imageRemove: 'Прибрати',
    imageUploading: 'Завантажуємо…',
    imageAlt: (name: string) => `Картинка послуги «${name}»`,
    imageErrors: {
      type: 'Підходить лише PNG, JPEG або WebP',
      size: 'Файл більший за 5 МБ',
      empty: 'Файл порожній',
    } as Record<string, string>,
    ages: 'Вік',
    ageFrom: 'Вік від',
    ageTo: 'Вік до',
    ageUnit: 'р.',
    ageHint: 'Повних років на день заняття, включно. Порожньо — без обмеження. Вік перевіряється при кожному записі за датою народження з картки клієнта.',
    ageInvalid: 'Цілі роки від 0 до 120',
    ageOrder: '«Вік від» не може бути більшим за «Вік до»',
    online: 'Продається в застосунку',
    onlineHint: 'Клієнт сам купує в iOS-застосунку й платить у клубі.',
    onlineNeedsPrice: 'Без ціни послуга в застосунку не продається.',
    onlineByPass: 'Без ціни клієнт записується в застосунку лише за своїм абонементом клубу.',
    save: 'Зберегти',
    create: 'Створити послугу',
    saving: 'Зберігаємо…',
    createdNext: 'Послугу створено. Тепер додайте виконавців і їхню оплату.',
    providers: 'Виконавці',
    providersHint: 'Хто надає послугу. Власна ціна — для старшого тренера; порожньо — ціна послуги.',
    providersAfterCreate: 'Виконавців можна додати після створення послуги.',
    noProviders: 'Ще нікого. Без виконавців індивідуальну послугу не купити.',
    addProvider: 'Додати виконавця',
    pickMember: 'Хто',
    pickMemberPlaceholder: 'Оберіть співробітника',
    everyoneAdded: 'Усі співробітники вже серед виконавців.',
    ownPrice: 'Власна ціна',
    ownPriceShort: 'власна ціна',
    ownPriceNone: (price: string) => `ціна послуги, ${price}`,
    removeProvider: (name: string) => `Прибрати ${name}`,
    add: 'Додати',
    payout: 'Оплата виконавцю',
    payoutHint:
      'Скільки отримує виконавець за кожен продаж. Правило для людини важливіше за правило «для всіх».',
    payoutHidden: 'Оплату виконавців бачить роль із доступом до фінансів.',
    payoutReadOnly: 'Змінювати оплату може роль із правом «Співробітники: керування» на всю компанію.',
    noRules: 'Правил ще немає — продажі не будуть оплачені виконавцям (у звіті — «без ставки»).',
    noOwnRules: 'Окремих правил для цієї послуги немає — платять загальні правила нижче.',
    general: 'Загальне правило',
    generalHint: 'Діє для всіх послуг, де немає правила про саму послугу. Правило про цю послугу важливіше за нього.',
    forEveryone: 'Для всіх',
    forWhom: 'Для кого',
    mode: 'Як рахувати',
    modes: { percent: '% від ціни', fixed: 'Сума за продаж', hourly: 'За годину' },
    amount: 'Сума',
    percent: 'Відсоток',
    amountInvalid: 'Вкажіть суму цифрами',
    percentInvalid: 'Від 0 до 100',
    addRule: 'Додати правило',
    saveRule: 'Зберегти правило',
    retireRule: 'Прибрати правило',
    groupNote: 'Групове заняття оплачується лише сумою або відсотком за кожного клієнта.',
  },

  payout: {
    percent: (pct: string) => pct,
    fixed: (amount: string) => amount,
    hourly: (amount: string) => `${amount}/год`,
    line: (who: string, what: string) => `${who}: ${what}`,
    window: (what: string, from: string, to: string) => `${what} (${from}–${to})`,
  },

  hours: {
    member: 'Виконавець',
    pickMember: 'Оберіть, чий графік',
    week: 'Тиждень',
    weekHint: 'Час — місцевий час клубу. Ніч через північ — два проміжки: до 24:00 і від 00:00.',
    dayOff: 'Вихідний',
    add: 'Додати час',
    from: 'З',
    to: 'До',
    save: 'Зберегти',
    remove: (range: string) => `Прибрати ${range}`,
    edit: (range: string) => `Змінити ${range}`,
    invalid: 'Кінець має бути пізніше за початок',
    noMembers: 'У компанії ще немає співробітників',
    readOnly: 'Графік змінює керуючий. Ви бачите свій.',
    timeOff: 'Відсутності',
    timeOffHint: 'Відпустка, лікарняний, «у вівторок після 16:00 мене немає». На весь час відсутності не запишуть.',
    timeOffNone: 'Попереду відсутностей немає.',
    timeOffAdd: 'Додати відсутність',
    timeOffFrom: 'Початок',
    timeOffTo: 'Кінець',
    timeOffNote: 'Примітка',
    timeOffNotePlaceholder: 'Відпустка',
    timeOffRemove: 'Прибрати відсутність',
    timeOffBooked: 'Записи, що вже є в цей час, не скасовуються — перевірте календар.',
  },

  analytics: {
    month: 'Місяць',
    prevMonth: 'Попередній місяць',
    nextMonth: 'Наступний місяць',
    revenue: 'Виручка',
    revenueNote: 'отримано від клієнтів',
    payout: 'Виплати',
    payoutNote: (planned: string) => `ще заплановано ${planned}`,
    payoutOwn: 'Ви заробили',
    company: 'Компанії',
    companyNote: 'виручка мінус виплати',
    unpaid: 'Не оплачено',
    unpaidNote: 'клієнти ще мають заплатити',
    open: (what: string) => `Відкрити рядки: ${what}`,
    byProvider: 'За виконавцями',
    byService: 'За послугами',
    byDay: 'Виручка по днях',
    byDaySummary: 'Виручка по днях місяця',
    dayLabel: (day: string, amount: string) => `${day}: ${amount}`,
    tableDay: 'День',
    tableRevenue: 'Виручка',
    noRevenue: 'Цього місяця оплат ще не було.',
    columns: {
      provider: 'Виконавець',
      service: 'Послуга',
      sales: 'Продажі',
      revenue: 'Виручка',
      payout: 'Виплата',
      company: 'Компанії',
      unpaid: 'Не оплачено',
    },
    noRate: (n: number) => `${n} без ставки`,
    noShowPayout: (amount: string) => `за неявки ${amount} — окремо`,
    byPass: (n: number) => `${n} за абонементом`,
    empty: 'Цього місяця продажів немає',
    emptyMessage: 'Тут зʼявляться записи й заняття з ціною або виплатою виконавцю.',
    totals: 'Усього',
    rowsTitle: 'Рядки продажів',
    rowsCount: (n: number) => rows(n),
    rowsEmpty: 'Немає рядків.',
    rowsNote: 'Кожен рядок — один запис. Сума цих рядків і є число у звіті.',
    rowColumns: { when: 'Коли', client: 'Клієнт', price: 'Ціна', payout: 'Виконавцю' },
    accrualStatus: {
      planned: 'Заплановано',
      confirmed: 'Відбулося',
      no_show: 'Неявка',
      cancelled: 'Скасовано',
      no_rate: 'Без ставки',
      no_payout: 'Без виплати',
    } as Record<string, string>,
  },

  errors: {
    unknownClassType: 'Такої послуги немає або її знято з продажу',
    notPersonal: 'Це групова послуга — на неї записують на заняття за розкладом',
    noProviders: 'У цієї послуги немає виконавців — додайте їх у вкладці «Послуги»',
    notAProvider: 'Ця людина не надає цю послугу (або вже не працює в компанії)',
    branchClosed: 'Цього дня клуб зачинений',
    outsideWorkingHours: 'Цей час поза робочими годинами виконавця',
    providerOff: 'Виконавець у цей час відсутній',
    providerBusy: 'Цей час уже зайнятий — виконавця щойно записали або в нього інше заняття. Оберіть інший час',
    resourceBusy: 'Приміщення в цей час зайняте',
    discountOverLimit: 'Знижка більша, ніж дозволяє ваша роль',
    refundOverLimit: 'Сума повернення більша за ліміт вашої ролі — попросіть керуючого',
    notPayable: 'За цей запис нічого сплачувати — його вже оплачено, скасовано або він за абонементом',
    notRefundable: 'Повернути можна лише оплату за скасований запис',
    overlappingHours: 'Цей проміжок перетинається з іншим того ж дня',
    invalidRange: 'Кінець має бути пізніше за початок',
    notAMember: 'Ця людина не працює в компанії',
    unknownBooking: 'Такого запису вже немає. Оновіть сторінку',
    unknownSession: 'Такого заняття вже немає. Оновіть сторінку',
    unknownWorkingHours: 'Цього проміжку вже немає. Оновіть сторінку',
    unknownTimeOff: 'Цієї відсутності вже немає. Оновіть сторінку',
    rateRetired: 'Це правило вже прибрано — додайте нове',
    unknownRate: 'Такого правила вже немає. Оновіть сторінку',
    passRequired: 'Ця послуга лише за абонементом',
    sessionCancelled: 'Це заняття скасоване',
    sessionInThePast: 'Цей час уже минув',
    bookingNotOpenYet: 'Запис на цей час ще не відкрито',
    bookingClosed: 'Запис на цей час уже закрито',
    alreadyBooked: 'Цей клієнт уже записаний сюди',
    sessionFull: 'Місць уже немає',
    tooManyNoShows: 'У клієнта забагато неявок — запис тимчасово закрито',
    certificateExpired: 'Медична довідка клієнта прострочена',
    clientsUnavailable: 'Не вдалося перевірити клієнта. Спробуйте ще раз',
    sessionChanged: 'Заняття щойно змінили — оновіть і спробуйте ще раз',
    trainerBusy: 'Тренер у цей час уже веде інше заняття',
    capacityExceedsResource: 'У залі менше місць, ніж указано для заняття',
    capacityRequired: 'Вкажіть кількість місць — без залу її ні звідки взяти',
    unknownResource: 'Такого залу немає. Оновіть сторінку',
    unknownTemplate: 'Цього заняття в розкладі вже немає. Оновіть сторінку',
    templateRetired: 'Це заняття вже знято з розкладу — додайте нове',
    slotInvalidRange: '«Діє до» має бути не раніше за «Діє з»',
    imageTooLarge: 'Картинка більша за 5 МБ',
    imageType: 'Підходить лише PNG, JPEG або WebP',
    payrollUnavailable: 'Не вдалося дістати виплати. Спробуйте ще раз',
  },
}
